import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { BackgroundStyle } from '@/store/useUIStore';
import { ColorSwatch } from './ColorSwatch';
import { SliderControl } from './SliderControl';

const BACKGROUND_STYLES = [
    { id: 'liquid', name: 'Liquid' },
    { id: 'ascii', name: 'ASCII' },
    { id: 'particles', name: 'Particles' },
    { id: 'topology', name: 'Topology' },
    { id: 'waveform', name: 'Waveform' },
    { id: 'geometric', name: 'Geometric' },
    { id: 'rain', name: 'Rain' },
    { id: 'orbit', name: 'Cell' },
    { id: 'cube3d', name: 'Cube 3D' },
    { id: 'blank', name: 'Blank' }
] as const;

type BackgroundTabProps = {
    backgroundStyle: BackgroundStyle;
    onBackgroundStyleChange: (style: BackgroundStyle) => void;
    activeColorPicker: 'background' | 'secondary' | 'topNav' | 'sidebar' | 'ui' | 'text';
    tempColor: string;
    tempSecondaryColor: string;
    onColorPickerChange: (picker: 'background' | 'secondary') => void;
    onColorChange: (color: string) => void;
    onMouseDown: () => void;
    onMouseUp: () => void;
    onTouchEnd: () => void;
    isAdvancedOpen: boolean;
    onAdvancedToggle: () => void;
    backgroundBrightness: number;
    onBrightnessChange: (value: number) => void;
    backgroundTransparency: number;
    onTransparencyChange: (value: number) => void;
    backgroundSaturation: number;
    onSaturationChange: (value: number) => void;
    backgroundScale: number;
    onScaleChange: (value: number) => void;
    backgroundAnimationSpeed: number;
    onAnimationSpeedChange: (value: number) => void;
};

export const BackgroundTab: React.FC<BackgroundTabProps> = ({
    backgroundStyle,
    onBackgroundStyleChange,
    activeColorPicker,
    tempColor,
    tempSecondaryColor,
    onColorPickerChange,
    onColorChange,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    isAdvancedOpen,
    onAdvancedToggle,
    backgroundBrightness,
    onBrightnessChange,
    backgroundTransparency,
    onTransparencyChange,
    backgroundSaturation,
    onSaturationChange,
    backgroundScale,
    onScaleChange,
    backgroundAnimationSpeed,
    onAnimationSpeedChange
}) => (
    <>
        {/* Style Selection */}
        <div className="p-3 border-b border-zinc-700">
            <div className="text-xs text-zinc-400 mb-2">Background Style</div>
            <select
                value={backgroundStyle}
                onChange={(e) => onBackgroundStyleChange(e.target.value as BackgroundStyle)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-300 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--accent-1))]/50"
            >
                {BACKGROUND_STYLES.map((style) => (
                    <option key={style.id} value={style.id}>
                        {style.name}
                    </option>
                ))}
            </select>
        </div>

        {/* Color Controls */}
        <div className="p-3 space-y-3">
            {/* Visual Effects Color */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-400">Effects Color</span>
                    <ColorSwatch
                        color={tempColor}
                        isActive={activeColorPicker === 'background'}
                        onClick={() => onColorPickerChange('background')}
                        title="Click to edit the color of visual effects (particles, waves, etc.)"
                    />
                </div>
            </div>

            {/* Background Color */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-zinc-400">Background</span>
                    <ColorSwatch
                        color={tempSecondaryColor}
                        isActive={activeColorPicker === 'secondary'}
                        onClick={() => onColorPickerChange('secondary')}
                        title="Click to edit the canvas background color"
                    />
                </div>
            </div>
        </div>

        {/* Color Picker */}
        <div className="flex justify-center p-3">
            <HexColorPicker
                color={
                    activeColorPicker === 'secondary' ? tempSecondaryColor : tempColor
                }
                onChange={onColorChange}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onTouchEnd={onTouchEnd}
                className="rounded-lg"
            />
        </div>

        {/* Advanced Options Toggle */}
        <div className="border-t border-zinc-700">
            <button
                onClick={onAdvancedToggle}
                className="w-full p-3 flex items-center justify-between text-xs text-zinc-400 hover:text-zinc-300"
            >
                <span className="font-medium">Advanced Options</span>
                {isAdvancedOpen ? (
                    <ChevronUp className="h-4 w-4" />
                ) : (
                    <ChevronDown className="h-4 w-4" />
                )}
            </button>

            {/* Collapsible Sliders */}
            {isAdvancedOpen && (
                <div className="px-3 pb-3 space-y-3">
                    <SliderControl
                        label="Brightness"
                        value={backgroundBrightness}
                        min={0}
                        max={2}
                        step={0.1}
                        onChange={onBrightnessChange}
                    />

                    <SliderControl
                        label="Transparency"
                        value={backgroundTransparency}
                        min={0}
                        max={1}
                        step={0.05}
                        onChange={onTransparencyChange}
                    />

                    <SliderControl
                        label="Saturation"
                        value={backgroundSaturation}
                        min={0}
                        max={2}
                        step={0.1}
                        onChange={onSaturationChange}
                    />

                    <SliderControl
                        label="Scale"
                        value={backgroundScale}
                        min={0.1}
                        max={3.0}
                        step={0.1}
                        onChange={onScaleChange}
                        formatValue={(v) => `${v.toFixed(1)}x`}
                    />

                    <SliderControl
                        label="Animation Speed"
                        value={backgroundAnimationSpeed}
                        min={0}
                        max={4}
                        step={0.1}
                        onChange={onAnimationSpeedChange}
                        formatValue={(v) => `${v.toFixed(1)}x`}
                    />
                </div>
            )}
        </div>
    </>
);